/**
 * Colour for a score, judged against the column it sits in rather than against a fixed
 * number. A competition's scores are whatever that competition's tasks produced: 0.085
 * was the winning total in CoT-Compression-9, and an absolute scale that calls anything
 * under 0.25 "weak" paints the entire board red and tells the reader nothing about who
 * did well. Relative banding is what the production dashboard has always done.
 */
export interface Scale {
  min: number;
  max: number;
}

export type Band = "top" | "high" | "mid" | "low" | "none";

/** Null when nothing in the column was measured - the caller then has no scale to use. */
export function scaleOf(values: (number | null | undefined)[]): Scale | null {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const v of values) {
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return Number.isFinite(min) ? { min, max } : null;
}

export function band(value: number | null | undefined, scale: Scale | null): Band {
  if (typeof value !== "number" || !Number.isFinite(value)) return "none";
  // One distinct value in the column, or none to compare against: it is the best there is.
  if (!scale || scale.max <= scale.min) return "top";
  const ratio = (value - scale.min) / (scale.max - scale.min);
  if (ratio >= 0.75) return "top";
  if (ratio >= 0.5) return "high";
  return ratio >= 0.25 ? "mid" : "low";
}

/**
 * A score judged on its own rather than against its neighbours. A platform score is
 * centred on zero: positive means compression beat the baseline on that task, negative
 * means it cost something. That is what the colour should say on the miner's own page,
 * where the comparison is with the baseline and not with other miners.
 *
 * Banding three category cards against each other would paint one of them red however
 * well all three did - with three values there is always a worst one.
 */
export function signBand(value: number | null | undefined): Band {
  if (typeof value !== "number" || !Number.isFinite(value)) return "none";
  return value >= 0 ? "top" : "low";
}
